/* @flow */
import cx from 'classnames';
import * as React from 'react';

import ClickEventTrigger from 'components/ClickEventTrigger.jsx';
import getPropClasses from 'lib/getPropClasses';
import LinkBase from 'components/LinkBase.jsx';
import LoadingIndicator from 'components/LoadingIndicator.jsx';
import tooltipable from 'lib/tooltipable';

import './styles/Button.scss';

import type { Props as LinkBaseProps } from 'components/LinkBase.jsx';

const propsToClasses = {
  level: {
    primary: 'Button--primary',
    secondary: 'Button--secondary',
    text: 'Button--text',
    icon: 'Button--icon',
  },
  color: {
    lava: 'Button--lava',
    caribbean: 'Button--caribbean',
    sunrise: 'Button--sunrise',
    shamrock: 'Button--shamrock',
    jungle: 'Button--jungle',
    brand: 'Button--brand',
    violet: 'Button--violet',
    pitch: 'Button--pitch',
    thunder: 'Button--thunder',
    eclipse: 'Button--eclipse',
    koala: 'Button--koala',
    'polar-bear': 'Button--polar-bear',
    white: 'Button--white',
    cactus: 'Button--cactus',
  },
  shape: {
    circle: 'Button--circle',
  },
  size: {
    large: 'Button--large',
    small: 'Button--small',
    xsmall: 'Button--xsmall',
    xxsmall: 'Button--xxsmall',
    xxxsmall: 'Button--xxxsmall',
  },
  weight: {
    normal: 'Button--normal',
    semibold: 'Button--semibold',
  },
  full: 'Button--full',
};

export type Props = {
  ...LinkBaseProps,
  level?: 'primary' | 'secondary' | 'text' | 'icon',
  color?:
    | 'lava'
    | 'sunrise'
    | 'shamrock'
    | 'jungle'
    | 'caribbean'
    | 'brand'
    | 'violet'
    | 'pitch'
    | 'thunder'
    | 'eclipse'
    | 'koala'
    | 'polar-bear'
    | 'white'
    | 'cactus',
  'aria-label'?: string,
  'data-test'?: string,
  'data-test-selector'?: string,
  eventElementName?: string,
  eventKey?: string,
  eventProperties?: Object,
  eventScreenName?: string,
  full: boolean,
  id?: string,
  inline: boolean,
  loading: boolean,
  shape?: 'circle',
  size?: 'large' | 'small' | 'xsmall' | 'xxsmall' | 'xxxsmall',
  type?: string,
  weight?: 'normal' | 'semibold',
};

class Button extends React.Component<Props> {
  static defaultProps = {
    ...LinkBase.defaultProps,
    full: false,
    inline: false,
    level: 'primary',
    loading: false,
    size: 'small',
  };

  isDisabled = () => {
    return this.props.disabled || this.props.loading;
  };

  renderChildren = () => {
    const lightBackground = this.props.color === 'white' || this.props.level === 'secondary';

    if (this.props.loading) {
      const color = lightBackground ? 'eclipse' : 'white';
      return <LoadingIndicator size={this.props.size} color={color} />;
    } else {
      return this.props.children;
    }
  };

  render() {
    const hasTarget = this.props.to || this.props.href;
    const Component = hasTarget ? LinkBase : 'button';

    return (
      <ClickEventTrigger
        eventElementName={this.props.eventElementName}
        eventKey={this.props.eventKey}
        eventProperties={this.props.eventProperties}
        eventScreenName={this.props.eventScreenName}>
        <Component
          id={this.props.id}
          type={this.props.type}
          target={this.props.target}
          href={this.props.href}
          to={this.props.to}
          aria-label={this.props['aria-label']}
          download={this.props.download}
          onMouseEnter={this.props.onMouseEnter}
          onMouseLeave={this.props.onMouseLeave}
          onMouseMove={this.props.onMouseMove}
          data-test-selector={this.props['data-test-selector']}
          data-test={this.props['data-test']}
          onClick={this.props.onClick}
          disabled={this.isDisabled()}
          className={cx(
            'Button',
            this.props.className,
            {
              'Button--inline': this.props.inline,
              'Button--disabled': this.isDisabled(),
            },
            getPropClasses(this.props, propsToClasses)
          )}>
          {this.renderChildren()}
        </Component>
      </ClickEventTrigger>
    );
  }
}

export { Button };
export default tooltipable(Button);
